/**
 * StaffNoticeAcknowledgment — one Revia staff member's acknowledgment of one
 * version of the staff data-protection notice, at
 * `workspaces/revia/staffNotices/{uid}/versions/{noticeVersion}`. The
 * backoffice admits a staff member only once the document for
 * `STAFF_NOTICE_VERSION` (`staff.const.ts`) exists under their uid.
 *
 * Written once by the staff member themselves, never changed and never
 * removed: the record is the evidence that this person was shown this text.
 *
 * Fields justified:
 *   noticeVersion  — the version acknowledged; equals the `{noticeVersion}`
 *                    path segment, so a document read on its own still says
 *                    what it acknowledges.
 *   acknowledgedAt — when; the rule pins it to the server's clock, so the
 *                    client cannot backdate it.
 *
 * Excluded:
 *   uid            — the `{uid}` path segment.
 *   userAgent / IP — the acknowledgment needs neither; not collected.
 *   notice text    — the version names it; the text lives with the notice.
 *
 * Access:
 *   create — the signed-in user, for their own `{uid}` only, signed in with a
 *            second factor (the `mfa` claim); `noticeVersion` equals the path
 *            segment, `acknowledgedAt` is `request.time`, and no other key.
 *   read   — the owner, or Revia staff (`STAFF_PERMISSION` held in
 *            `workspaces/revia`) with a second factor. Emitted as a get only:
 *            the `noticeVersion` path-variable leaf names one document, which
 *            a list does not bind.
 *   list   — Revia staff with a second factor.
 *   update / delete — nobody.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { Workspace } from "@momo/types";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { REVIA_ORG_ID, STAFF_PERMISSION } from "./staff.const.ts";

export const StaffNoticeAcknowledgmentSchema = z
	.object({
		noticeVersion: z
			.string()
			.min(1)
			.describe("the version of the staff notice acknowledged"),
		acknowledgedAt: z
			.custom<Timestamp>((value) => value != null)
			.describe("when the staff member acknowledged the notice"),
	})
	.strict();
export type StaffNoticeAcknowledgment = z.infer<
	typeof StaffNoticeAcknowledgmentSchema
>;

const staff = { workspace: REVIA_ORG_ID };

export const StaffNoticeAcknowledgment = entity<Workspace.Actor>()({
	schema: StaffNoticeAcknowledgmentSchema,
	actions: ["read", "list", "create"] as const,
})
	.pipe(({ can, hasPermission, and, or, pathVar }) => [
		can("read").when((_data, me) =>
			and(
				pathVar("noticeVersion").exists(),
				or(
					me.uid.eq(pathVar("uid")),
					and(
						hasPermission(me.permissions, STAFF_PERMISSION, staff),
						me.mfa.eq(true),
					),
				),
			),
		),
		can("list").when((_data, me) =>
			and(
				hasPermission(me.permissions, STAFF_PERMISSION, staff),
				me.mfa.eq(true),
			),
		),
		can("create")
			.when((data, me) =>
				and(
					me.uid.eq(pathVar("uid")),
					me.mfa.eq(true),
					data.noticeVersion.eq(pathVar("noticeVersion")),
				),
			)
			.writes((w) =>
				w
					.set({ acknowledgedAt: "serverTime" })
					.keys(["noticeVersion", "acknowledgedAt"]),
			),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: `workspaces/${REVIA_ORG_ID}/staffNotices/{uid}/versions/{noticeVersion}`,
		}),
	);
